import React, { useState, forwardRef, useImperativeHandle } from "react";

interface InvoiceProps {
    invoice: string;
    className?: string;
}

export interface InvoiceHandle {
    handleCopy: () => Promise<void>;
}

const Invoice = forwardRef<InvoiceHandle, InvoiceProps>(({ invoice, className }, ref) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(invoice);
            setCopied(true);
            // Add a subtle animation class when copying
            setTimeout(() => setCopied(false), 1500);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    useImperativeHandle(ref, () => ({
        handleCopy
    }));

    const formatInvoice = (invoice: string) => {
        if (invoice.length <= 32) return invoice;
        const firstPart = invoice.slice(0, 4);
        const secondPart = invoice.slice(4, 16);
        const lastPart = invoice.slice(-16);
        const lastFour = lastPart.slice(-4);
        const beforeLastFour = lastPart.slice(0, 12);
        return (
            <>
                <span>{firstPart}</span>
                <span className="text-brand-color-2">{secondPart}</span>
                <span className="text-brand-color-2">...</span>
                <span className="text-brand-color-2">{beforeLastFour}</span>
                <span>{lastFour}</span>
            </>
        );
    };

    return (
        <div>
            <div 
                className={`flex items-center gap-2 cursor-pointer ${className || ''} ${copied ? 'invisible h-[1px]' : ''}`}
                onClick={() => !copied && handleCopy()}
            >
                <span className="break-words [word-break:break-word]" title={invoice}>
                    {formatInvoice(invoice)}
                </span>
                <i 
                    className="material-icons text-[18px]"
                    title="Copy to clipboard"
                >
                    file_copy
                </i>
            </div>
            <div className={`flex items-center gap-2 ${className || ''} ${!copied ? 'invisible h-[1px]' : ''}`}>
                <span className="break-words [word-break:break-word]">
                    Copied to clipboard
                </span>
                <i 
                    className="material-icons text-[18px]"
                >
                    check
                </i>
            </div>
        </div>
    );
});

Invoice.displayName = 'Invoice';

export default Invoice;